import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

// --
// -- A dated line chart, drawn by hand.
// --
// -- No charting library: the app pins its dependencies deliberately, and a
// -- couple of line charts do not justify adding one.
// --
// -- The two series colours are the validated categorical slots 1 and 2,
// -- checked against this app's white card surface rather than a reference
// -- one: worst-pair CVD deltaE 24.7, normal-vision 33.6, both over 3:1. Do not
// -- substitute colours here without re-running that check.
// --
export const SERIES_COLORS = ['#2a78d6', '#eb6834'];

// Chrome comes from the app's own tokens so the chart sits inside the card
// rather than beside it. Text never wears a series colour - the swatch next to
// it carries the identity.
const INK = '#333333';
const INK_MUTED = '#6D6D6D';
const GRID = '#DDDDDD';
const SURFACE = '#ffffff';

const PLOT_HEIGHT = 190;
const AXIS_BAND = 26; // the x labels live here, inside the sized box
const PAD = { top: 14, right: 18, bottom: 0 };
/** Roughly the width of one digit at 11px in the UI sans. */
const TICK_CHAR_PX = 6.4;

export interface ISeries {
  name: string;
  /** One value per point, aligned with `points`; null leaves a gap. */
  values: (number | null)[];
}

export interface ITimeSeriesChartProps {
  title: string;
  /** Point labels, already in ascending order. */
  points: { label: string; at: number }[];
  series: ISeries[];
  /** How a value reads in the tooltip and the end label. */
  format: (value: number) => string;
  /**
   * How a value reads on the y axis. Defaults to `format`.
   *
   * Money wants a shorter form here - "$100,000.00" is wider than the gutter
   * any sane chart gives its axis, and a full currency string on every tick
   * crowds the plot for no gain.
   */
  formatTick?: (value: number) => string;
  /** Held at reduced opacity while refetching, rather than flashing a skeleton. */
  isFetching?: boolean;
}

/** Clean axis steps - 0 / 250 / 500, never 0 / 237 / 474. */
const niceTicks = (max: number, count = 4): number[] => {
  if (!Number.isFinite(max) || max <= 0) return [0, 1];

  const rough = max / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) ?? magnitude * 10;

  const ticks: number[] = [];
  for (let value = 0; value <= max + step / 2; value += step) ticks.push(value);
  return ticks.length > 1 ? ticks : [0, step];
};

const TimeSeriesChart: FC<ITimeSeriesChartProps> = ({
  title, points, series, format, formatTick, isFetching = false,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);
  const [active, setActive] = useState<number | null>(null);

  // -- Measured rather than scaled: a viewBox stretched to fit would distort
  // -- the labels with it.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(([entry]) => {
      const next = entry?.contentRect?.width;
      if (next) setWidth(Math.max(320, Math.round(next)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const height = PLOT_HEIGHT + AXIS_BAND;
  const plotH = PLOT_HEIGHT - PAD.top;

  const max = useMemo(() => {
    const values = series.flatMap((s) => s.values).filter((v): v is number => v !== null && Number.isFinite(v));
    return values.length ? Math.max(...values) : 0;
  }, [series]);

  const ticks = useMemo(() => niceTicks(max), [max]);
  const top = ticks[ticks.length - 1] || 1;

  const tickText = formatTick || format;
  // -- Measured from the widest label rather than a fixed guess: a fixed
  // -- gutter is how axis labels end up hanging off the left of the chart.
  const padLeft = useMemo(() => {
    const widest = Math.max(...ticks.map((t) => tickText(t).length), 1);
    return Math.ceil(widest * TICK_CHAR_PX) + 12;
  }, [ticks, tickText]);

  // Declared after padLeft: the gutter decides how much width the plot gets.
  const plotW = Math.max(1, width - padLeft - PAD.right);

  // -- Positioned by date, not by index: the report only returns days that had
  // -- orders, so an index scale would draw a quiet week as though it were
  // -- busy.
  const span = useMemo(() => {
    if (points.length < 2) return { min: 0, range: 1 };
    const min = points[0].at;
    return { min, range: Math.max(1, points[points.length - 1].at - min) };
  }, [points]);

  const xOf = useCallback((i: number) => {
    if (points.length < 2) return padLeft + plotW / 2;
    return padLeft + ((points[i].at - span.min) / span.range) * plotW;
  }, [points, plotW, span, padLeft]);

  const yOf = useCallback((value: number) => PAD.top + plotH - (value / top) * plotH, [plotH, top]);

  const pathOf = (values: (number | null)[]) => {
    let path = '';
    let pen = false;
    values.forEach((value, i) => {
      if (value === null || !Number.isFinite(value)) { pen = false; return; }
      path += `${pen ? 'L' : 'M'}${xOf(i).toFixed(1)},${yOf(value).toFixed(1)}`;
      pen = true;
    });
    return path;
  };

  const lastDefined = (values: (number | null)[]) => {
    for (let i = values.length - 1; i >= 0; i--) if (values[i] !== null) return i;
    return -1;
  };

  // -- The crosshair finds the X: readers aim at a date, never at a 2px line.
  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!points.length) return;
    const box = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - box.left;

    let nearest = 0;
    let best = Infinity;
    points.forEach((_, i) => {
      const distance = Math.abs(xOf(i) - x);
      if (distance < best) { best = distance; nearest = i; }
    });
    setActive(nearest);
  };

  const onKeyDown = (e: React.KeyboardEvent<SVGSVGElement>) => {
    if (!points.length) return;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    setActive((prev) => {
      const from = prev ?? (e.key === 'ArrowRight' ? -1 : points.length);
      return Math.min(points.length - 1, Math.max(0, from + (e.key === 'ArrowRight' ? 1 : -1)));
    });
  };

  // --
  // -- One day is not a trend. A lone point on an empty plot is the "is it
  // -- even a chart" case from the form guide - the table below already shows
  // -- that single row, so the chart stands aside rather than drawing a dot in
  // -- a field of white.
  // --
  if (points.length < 2) return null;

  // -- Enough labels to orient, never so many they collide.
  const labelEvery = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(plotW / 78))));
  const single = series.length === 1;

  return (
    <div className="flex-1 min-w-[320px]" ref={wrapRef}>
      <div className="flex items-baseline justify-between gap-4 mb-1">
        <div className="font-semibold">{title}</div>
        {/* A legend is always present for two or more series; one series is
            named by the title, so a single swatch would just restate it. */}
        {!single && (
          <div className="flex gap-3 text-text_pale">
            {series.map((s, i) => (
              <span key={s.name} className="flex items-center gap-1.5 whitespace-nowrap">
                <svg width="14" height="2" aria-hidden="true">
                  <rect width="14" height="2" rx="1" fill={SERIES_COLORS[i % SERIES_COLORS.length]} />
                </svg>
                {s.name}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className={`relative transition-opacity ${isFetching ? 'opacity-40' : ''}`}>
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={`${title}. ${points.length} days. Full values are in the table below.`}
          tabIndex={0}
          className="outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          onPointerMove={onMove}
          onPointerLeave={() => setActive(null)}
          onKeyDown={onKeyDown}
          onBlur={() => setActive(null)}
        >
          {/* Gridlines and axis: solid hairlines, one step off the surface. */}
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={padLeft} x2={padLeft + plotW} y1={yOf(tick)} y2={yOf(tick)}
                stroke={GRID} strokeWidth={1} shapeRendering="crispEdges"
              />
              <text
                x={padLeft - 8} y={yOf(tick) + 4} textAnchor="end"
                fill={INK_MUTED} fontSize={11} style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {tickText(tick)}
              </text>
            </g>
          ))}

          {points.map((point, i) => (i % labelEvery === 0 ? (
            <text
              key={point.label} x={xOf(i)} y={PLOT_HEIGHT + 17} textAnchor="middle"
              fill={INK_MUTED} fontSize={11} style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {point.label.slice(5)}
            </text>
          ) : null))}

          {active !== null && (
            <line
              x1={xOf(active)} x2={xOf(active)} y1={PAD.top} y2={PAD.top + plotH}
              stroke={INK_MUTED} strokeWidth={1} shapeRendering="crispEdges"
            />
          )}

          {series.map((s, si) => {
            const color = SERIES_COLORS[si % SERIES_COLORS.length];
            const end = lastDefined(s.values);
            return (
              <g key={s.name}>
                {/* A wash under a lone series; two lines share the plot and
                    stacked fills would just muddy each other. */}
                {single && end >= 0 && (
                  <path
                    d={`${pathOf(s.values)}L${xOf(end).toFixed(1)},${(PAD.top + plotH).toFixed(1)}L${xOf(0).toFixed(1)},${(PAD.top + plotH).toFixed(1)}Z`}
                    fill={color} fillOpacity={0.1}
                  />
                )}
                <path
                  d={pathOf(s.values)} fill="none" stroke={color}
                  strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
                />
                {end >= 0 && (
                  <circle
                    cx={xOf(end)} cy={yOf(s.values[end] as number)} r={4}
                    fill={color} stroke={SURFACE} strokeWidth={2}
                  />
                )}
                {active !== null && s.values[active] !== null && (
                  <circle
                    cx={xOf(active)} cy={yOf(s.values[active] as number)} r={4}
                    fill={color} stroke={SURFACE} strokeWidth={2}
                  />
                )}
              </g>
            );
          })}

          {/* One direct label, on the series the chart is about - a number on
              every point goes unread, and two end labels collide. */}
          {(() => {
            const end = lastDefined(series[0].values);
            if (end < 0) return null;
            const y = yOf(series[0].values[end] as number);
            return (
              <text
                x={width - 2} y={Math.min(Math.max(y - 10, PAD.top + 9), PAD.top + plotH)}
                textAnchor="end" fill={INK} fontSize={11} fontWeight={600}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {tickText(series[0].values[end] as number)}
              </text>
            );
          })()}
        </svg>

        {active !== null && (
          <div
            className="pointer-events-none absolute z-10 rounded border border-border_color bg-white shadow-md px-3 py-2 whitespace-nowrap"
            style={{
              left: Math.min(Math.max(xOf(active) + 12, 0), Math.max(0, width - 190)),
              top: PAD.top,
            }}
          >
            <div className="text-text_pale mb-1">{points[active].label}</div>
            {series.map((s, si) => (
              <div key={s.name} className="flex items-center gap-2">
                <svg width="12" height="2" aria-hidden="true">
                  <rect width="12" height="2" rx="1" fill={SERIES_COLORS[si % SERIES_COLORS.length]} />
                </svg>
                {/* The value leads; the reader already knows the series. */}
                <span className="font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {s.values[active] === null ? '—' : format(s.values[active] as number)}
                </span>
                <span className="text-text_pale">{s.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeSeriesChart;
